/**
 * SegmentHeatmap — CSS grid heatmap table for gate pass rates by segment.
 * Column group selector: Placement / Objective / Dimension / Taxonomy / All.
 * "All" mode unions all columns with horizontal scroll (FR-24).
 */

import { useState } from 'react';

import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { useGateSummary } from '@/hooks/useDashboard';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import type { HeatmapColumnGroup } from '@/types/dashboard';

// ============================================================================
// HELPERS
// ============================================================================

function getHeatmapBg(passRate: number | undefined): string {
  if (passRate === undefined || passRate === null) return 'rgb(var(--color-surface-subtle))';
  if (passRate >= 80) return 'rgb(var(--color-success-strong) / 0.15)';
  if (passRate >= 60) return 'rgb(var(--color-warning-strong) / 0.15)';
  return 'rgb(var(--color-danger-strong) / 0.15)';
}

function getHeatmapText(passRate: number | undefined): string {
  if (passRate === undefined || passRate === null) return 'rgb(var(--color-text-muted))';
  if (passRate >= 80) return 'rgb(var(--color-success))';
  if (passRate >= 60) return 'rgb(var(--color-warning))';
  return 'rgb(var(--color-danger))';
}

// ============================================================================
// CONSTANTS
// ============================================================================

const COLUMN_GROUPS: { value: HeatmapColumnGroup; label: string }[] = [
  { value: 'placement', label: 'Placement' },
  { value: 'objective', label: 'Objective' },
  { value: 'dimension', label: 'Dimension' },
  { value: 'taxonomy', label: 'Taxonomy' },
  { value: 'all', label: 'All' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function SegmentHeatmap() {
  const { filters } = useDashboardContext();
  const [columnGroup, setColumnGroup] = useState<HeatmapColumnGroup>('placement');
  const { data } = useGateSummary(filters, columnGroup);

  const heatmapData = data?.heatmapData;
  if (!heatmapData || heatmapData.rows.length === 0) return null;

  // Use heatmap data from API — the backend handles column group switching
  const rows = heatmapData.rows;
  const columns = heatmapData.columns;
  const columnLabels: Record<string, string> = (heatmapData as unknown as Record<string, unknown>).columnLabels as Record<string, string> ?? {};

  // CSV export
  const csvHeaders = ['Gate', ...columns.map((col) => columnLabels[col] ?? col)];
  const csvRows = rows.map((row) => [
    row.gateName,
    ...columns.map((col) => row.cells[col] !== undefined ? row.cells[col] : 'N/A'),
  ]);

  return (
    <div className="rounded-xl bg-surface shadow-sm border border-border-subtle overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div className="text-sm font-semibold text-primary">Segment Heatmap</div>
        <div className="flex items-center gap-3">
          {/* Column group selector */}
          <div className="flex items-center gap-1 flex-wrap">
            {COLUMN_GROUPS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setColumnGroup(value)}
                className={cn(
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  columnGroup === value
                    ? 'bg-brand text-inverse'
                    : 'bg-surface-subtle text-secondary hover:bg-border-subtle'
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-2 text-xs text-secondary">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#22C55E' }} />
              &ge;80%
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
              60-79%
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#EF4444' }} />
              &lt;60%
            </span>
          </div>

          <CsvExport headers={csvHeaders} rows={csvRows} filename="segment-heatmap" />
        </div>
      </div>

      {/* Heatmap table with horizontal scroll for "All" mode */}
      <div className={cn('p-3', columnGroup === 'all' && 'overflow-x-auto')}>
        <table className="min-w-max text-xs border-collapse w-full">
          <thead>
            <tr>
              <th className="text-left text-secondary font-normal px-2 py-1 w-40 sticky left-0 bg-surface">
                Gate
              </th>
              {columns.map((col) => (
                <th key={col} className="text-secondary font-normal px-2 py-1 text-center">
                  <span className="text-[10px] bg-surface-subtle px-1.5 py-0.5 rounded whitespace-nowrap">
                    {columnLabels[col] ?? col}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={row.gateId} className={rowIdx % 2 === 1 ? 'bg-surface-subtle' : ''}>
                <td className="px-2 py-1.5 font-medium text-primary truncate max-w-[160px] sticky left-0 bg-inherit">
                  {row.gateName}
                </td>
                {columns.map((col) => {
                  const rate = row.cells[col];
                  return (
                    <td
                      key={col}
                      className="px-2 py-1.5 text-center font-medium"
                      style={{
                        backgroundColor: getHeatmapBg(rate),
                        color: getHeatmapText(rate),
                      }}
                      title={`Gate: ${row.gateName}, ${col}: ${rate !== undefined ? `${rate}%` : 'N/A'}`}
                    >
                      {rate !== undefined ? `${Number(rate).toFixed(0)}%` : '\u2014'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
